import React, { useLayoutEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDraggable } from '@dnd-kit/core';
import CardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import Tooltip from '../../../ui/Tooltip';
import { isTouchLike } from '../../../ui/pointerType';
import { DRAG_DISTANCE_PX, dragData, dragId } from '../hooks/usePlaytestDnd';
import { getPlayerColor } from '../utils/playerUtils';
import { badgeEntries, computePowerToughness, formatPowerToughness, primaryCounter } from '../utils/cardValues';
import { CARD_COUNTER_ICONS, COUNTER_LABELS } from '../utils/cardLabels';
import { boardCardRotation, cardImageProps, faceRotation } from '../utils/cardView';
import { chain, mergeHandlers } from '../utils/eventHandlers';
import './BattlefieldCard.css';

/**
 * Eine Karte auf der Brettfläche — reine Darstellung, kennt den Socket nicht.
 *
 *   <BattlefieldCard card={card} ownerPosition={1} viewerRotation={180} isOwn={false} sizePx={96}
 *     canAct isMobile={false} pingedBy={null} onTap={…} onPing={…} onBringToFront={…} onOpenMenu={…}
 *     onViewFlip={…} onHoverPreview={bind} />
 *
 * - `sizePx`: Kartenbreite in Pixeln (Höhe 7/5); die Lage setzt `Battlefield`.
 * - `viewerRotation` (0 | 180): Drehung, unter der der Betrachter diese Karte sieht
 *   (`getCardRotation`); getappt kommen 90° dazu, gedreht wird ein inneres Element, nicht die
 *   framer-motion-Hülle, deren Transform die Weg-Animation (`layoutId = instanceId`) belegt.
 * - Eigene Karte (`isOwn`, dazu `canAct` — false für Ausgeschiedene, die nur zusehen): ziehbar
 *   und fokussierbar (dnd-kit, `dragData(card, 'battlefield')`), Druck mit der Haupttaste →
 *   `onBringToFront(id)`, Klick → `onTap(id)`; ein Klick am Ende einer Zieh-Bewegung
 *   (> DRAG_DISTANCE_PX) zählt nicht. Fremde Karte bzw. ohne `canAct`: weder Ziehen, Tappen noch
 *   Fokus.
 * - Jede Karte: Doppelklick → `onPing(id)` (nicht auf dem Flip-Knopf: zweimal drehen ist kein
 *   Hinweis), Rechtsklick → `onOpenMenu(id, { x, y })` (Client-Koordinaten; das Menü der
 *   Brettfläche erhält das Ereignis nicht).
 * - Die sichtbare Seite kommt aus `card.transformedFace`. `onViewFlip` (nur gesetzt, solange die
 *   Großansicht dieser Karte steht) hängt am Umdreh-Knopf von `CardImage` und dreht nur die
 *   Großansicht; ohne `onViewFlip` gibt es keinen Knopf. Transformieren läuft über das Kartenmenü.
 *   Eine verdeckte eigene Karte (`CardImage.Back`) hat keinen Knopf, auch nicht bei stehender
 *   Großansicht: ein Knopf auf einem Kartenrücken ließe offen, ob er die Karte aufdeckt oder nur
 *   ansieht. R dreht ihre Großansicht trotzdem (Spec 2026-10-31-playtest-rueckseite-ansehen §9 P9).
 * - `onHoverPreview(card)` liefert Zeiger-Handler der schwebenden Vorschau (`useHoverPreview.bind`);
 *   verdeckte fremde Karten haben keine Vorschau.
 * - Verdeckt → `CardImage.Back` ohne Werte: auf dem Rücken stünde sonst die Stärke/Widerstandskraft
 *   der Vorderseite (eine verdeckte Karte ist eine 2/2). Token/Kopie/fremde Kontrolle sind
 *   `CardOverlays`-Badges oben links.
 * - Werte (Spec §14): Stärke/Widerstandskraft mit eingerechneten +1/+1- und −1/−1-Countern als
 *   Kästchen unten rechts über den gedruckten Werten (`card-pt`; darunter bleibt die
 *   Künstler-/©-Zeile frei, Abstände in `BattlefieldCard.css`), darüber am rechten Rand die übrigen
 *   Counter und Marken als Badges (`card-badges`, Symbol + Zahl). Die Werte-Ebene dreht mit dem
 *   Tappen und der Betrachter-Drehung, nicht aber mit der Lage der Seite (`faceRotation`): bei
 *   Battles und der umgedrehten Flip-Hälfte stehen die Werte aufrecht unten rechts, so wie die Karte
 *   liegt (`data-face-turn`). Die Größen folgen der kurzen Kartenseite (`cqmin`) mit Mindestgröße,
 *   damit sie herausgezoomt lesbar bleiben. Ist die Basis keine Zahl (`*`), zeigt das Kästchen die Basis und die Counter stehen
 *   als Badge. Bei `isOwn && canAct` ist das Kästchen selbst ein Knopf für +1/+1-Counter (Bedienung
 *   wie die Counter-Badges, auch ohne vorhandene Counter); −1/−1 bleibt im Kartenmenü.
 *   Druckt die sichtbare Seite statt Stärke/Widerstandskraft Loyalität oder Verteidigung
 *   (`primaryCounter`), steht an derselben Stelle der Hauptwert (`card-primary-counter`, Symbol +
 *   Zahl, ohne Marken 0), auf eigenen Karten ein Knopf für genau diesen Marken-Typ; die Badge-Spalte
 *   beginnt dann ebenfalls über ihm.
 * - Counter-Badges sind bei `isOwn && canAct` Knöpfe: Klick → `onCounterAdd(id, type)`, Rechtsklick →
 *   `onCounterRemove(id, type)` (kein Kartenmenü, kein Tappen, kein Ziehen); ihr `aria-label`
 *   nennt Wert und Bedienung, am Handy (`isMobile`) nur den Tipp. Bei Finger und Stift
 *   (`pointerType` ≠ mouse) gilt nur der Tipp (+1): Druck und Langdruck gehen an die Karte, deren
 *   Langdruck-Sheet das Verringern übernimmt. Fremde Karten und Marken-Badges bleiben Anzeige
 *   (`role="img"` mit `aria-label`, ebenso das Kästchen). Jeder Badge nennt seinen Namen als
 *   `ui/Tooltip`; fokussierbar sind Anzeige-Badges nur auf fremden Karten, in eigenen ist die Karte
 *   selbst der Tab-Halt. Bei Touch erscheint der Tooltip nie, der Langdruck zeigt also nur das Sheet.
 * - Passen nicht alle Badges in die Spalte, wird der letzte sichtbare zum Zähler „+n" der
 *   übrigen (`card-badges-more`, der Tooltip nennt sie). Das Kästchen ist golden umrandet, sobald
 *   +1/+1- oder −1/−1-Counter liegen, auch wenn sie sich aufheben.
 * - `pingedBy` (Teilnehmer-Position oder null) → Leuchtring außen um die Karte in der Farbe des
 *   Zeigenden, 2 s CSS-Animation.
 * - Prüfanker: `data-instance-id`, `data-tapped`, `data-pinged` (Position), `data-owner-position`;
 *   `data-no-pan` hält das Schwenken der Kamera von der Karte fern.
 */
export default function BattlefieldCard({
  card,
  ownerPosition,
  viewerRotation = 0,
  isOwn,
  canAct = true,
  isMobile = false,
  sizePx,
  pingedBy = null,
  onTap,
  onPing,
  onBringToFront,
  onOpenMenu,
  onViewFlip,
  onCounterAdd,
  onCounterRemove,
  onHoverPreview,
}) {
  const { instanceId } = card;
  const pointerStartRef = useRef(null);
  const acts = isOwn && canAct;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId(instanceId),
    data: dragData(card, 'battlefield'),
    disabled: !acts,
  });

  const imageProps = cardImageProps(card);
  const pinged = pingedBy !== null && pingedBy !== undefined;
  const previewHandlers = onHoverPreview && (!card.faceDown || isOwn) ? onHoverPreview(card) : null;
  const pointerHandlers = mergeHandlers(listeners, previewHandlers);

  const handlePointerDown = (e) => {
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    // Nur die Haupttaste: ein Rechtsklick öffnet das Menü und soll die Stapelung nicht ändern.
    if (acts && e.button === 0) onBringToFront?.(instanceId);
  };

  const handleClick = (e) => {
    const start = pointerStartRef.current;
    pointerStartRef.current = null;
    if (!acts) return;
    if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > DRAG_DISTANCE_PX) return;
    onTap?.(instanceId);
  };

  const handleDoubleClick = (e) => {
    if (e.target?.closest?.('button')) return;
    onPing?.(instanceId);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onOpenMenu?.(instanceId, { x: e.clientX, y: e.clientY });
  };

  const overlays = <StatusBadges card={card} />;
  const rotation = viewerRotation + boardCardRotation(card);

  return (
    <motion.div
      ref={setNodeRef}
      layoutId={instanceId}
      layoutDependency={`${card.position?.x},${card.position?.y}`}
      className={`battlefield-card${acts ? ' battlefield-card--own' : ''}${isDragging ? ' battlefield-card--dragging' : ''}`}
      style={{
        width: sizePx,
        ...(pinged && { '--battlefield-card-ping': getPlayerColor(pingedBy) }),
      }}
      data-testid="battlefield-card"
      data-instance-id={instanceId}
      data-tapped={card.tapped ? 'true' : 'false'}
      data-owner-position={ownerPosition}
      data-pinged={pinged ? pingedBy : undefined}
      data-no-pan=""
      aria-label={card.faceDown ? 'Verdeckte Karte' : imageProps.alt}
      {...(acts ? attributes : {})}
      {...pointerHandlers}
      onPointerDown={chain(pointerHandlers.onPointerDown, handlePointerDown)}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <div className="battlefield-card__body" style={{ transform: `rotate(${rotation}deg)` }}>
        {pinged && <div key={pingedBy} className="battlefield-card__ping" aria-hidden />}
        {card.faceDown ? (
          <CardImage.Back label="Verdeckte Karte">{overlays}</CardImage.Back>
        ) : (
          <CardImage
            {...imageProps}
            onFlip={onViewFlip}
          >
            {overlays}
          </CardImage>
        )}
        {!card.faceDown && (
          <CardValueMarks
            card={card}
            turn={faceRotation(card)}
            own={acts}
            isMobile={isMobile}
            sizePx={sizePx}
            onCounterAdd={acts ? onCounterAdd : undefined}
            onCounterRemove={acts ? onCounterRemove : undefined}
          />
        )}
      </div>
    </motion.div>
  );
}

function StatusBadges({ card }) {
  const status = [];
  if (card.isToken) status.push({ key: 'token', text: 'Token' });
  if (card.isCopy) status.push({ key: 'copy', text: 'Kopie' });
  if (card.controllerId != null && card.ownerId != null && String(card.controllerId) !== String(card.ownerId)) {
    status.push({ key: 'control', text: 'Kontrolle' });
  }
  if (status.length === 0) return null;
  return (
    <CardOverlays>
      <CardOverlays.Corner position="top-left">
        {status.map((b) => <CardOverlays.Badge key={b.key} passive>{b.text}</CardOverlays.Badge>)}
      </CardOverlays.Corner>
    </CardOverlays>
  );
}

const PT_COUNTERS = ['+1/+1', '-1/-1'];

/**
 * Wie viele Einträge vor dem „+n" stehen (null = alle passen). Gemessen wird mit allen Einträgen
 * an der Layout-Lage (`offset*`, ohne die Drehung der Karte): der erste Badge, der aus der Spalte
 * ragt, und alle danach fallen weg, der letzte sichtbare wird zum Zähler der weggefallenen.
 * Ändern sich Einträge oder Kartengröße, wird mit allen Einträgen neu gemessen.
 */
function useBadgeOverflow(columnRef, measureKey) {
  const [limit, setLimit] = useState(null);
  useLayoutEffect(() => {
    setLimit(null);
  }, [measureKey]);
  useLayoutEffect(() => {
    const column = columnRef.current;
    if (limit !== null || !column) return;
    // `offset*` und `client*` sind auf ganze Pixel gerundet: ein Badge, das bündig am Rand
    // steht, ragt rechnerisch bis zu einem Pixel hinaus.
    const fits = (el) => el.offsetLeft >= -1 && el.offsetTop >= -1
      && el.offsetLeft + el.offsetWidth <= column.clientWidth + 1
      && el.offsetTop + el.offsetHeight <= column.clientHeight + 1;
    const firstCut = Array.from(column.children).findIndex((el) => !fits(el));
    if (firstCut !== -1) setLimit(Math.max(firstCut - 1, 0));
  }, [columnRef, limit, measureKey]);
  return limit;
}

function CardValueMarks({ card, turn, own, isMobile, sizePx, onCounterAdd, onCounterRemove }) {
  const columnRef = useRef(null);
  const pt = computePowerToughness(card);
  const ptText = formatPowerToughness(pt);
  const entries = badgeEntries(card);
  const limit = useBadgeOverflow(columnRef, `${sizePx}|${entries.map((e) => `${e.key}:${e.text}`).join(',')}`);
  const primaryType = ptText ? null : primaryCounter(card);
  if (!ptText && primaryType === null && entries.length === 0) return null;
  const hasBox = Boolean(ptText) || primaryType !== null;
  const counters = card.counters || {};
  // Gold auch dann, wenn sich +1/+1 und −1/−1 aufheben: die Karte trägt Counter, die nur hier stehen.
  const modified = pt.modified !== null && (pt.delta !== 0 || PT_COUNTERS.some((type) => counters[type] > 0));
  const shown = limit === null ? entries : entries.slice(0, limit);
  const hidden = limit === null ? [] : entries.slice(limit);

  return (
    <div
      className={`battlefield-card__values${turn === 90 ? ' battlefield-card__values--landscape' : ''}`}
      style={{ '--card-values-turn': `${-turn}deg` }}
      data-face-turn={turn}
    >
      {entries.length > 0 && (
        <div
          ref={columnRef}
          className={`battlefield-card__badges${hasBox ? ' battlefield-card__badges--above-box' : ''}`}
          data-testid="card-badges"
        >
          {shown.map((entry) => (entry.kind === 'counter' && onCounterAdd ? (
            <CounterBadge
              key={entry.key}
              entry={entry}
              instanceId={card.instanceId}
              isMobile={isMobile}
              onCounterAdd={onCounterAdd}
              onCounterRemove={onCounterRemove}
            />
          ) : (
            <PassiveBadge
              key={entry.key}
              label={entry.label}
              ariaLabel={entry.text ? `${entry.label} ${entry.text}` : entry.label}
              focusable={!own}
            >
              <entry.icon className="battlefield-card__badge-icon" aria-hidden />
              {entry.text && <span>{entry.text}</span>}
            </PassiveBadge>
          )))}
          {hidden.length > 0 && (
            <PassiveBadge
              label={`Weitere: ${hidden.map((e) => (e.text ? `${e.label} ${e.text}` : e.label)).join(', ')}`}
              ariaLabel={`${hidden.length} weitere Werte`}
              focusable={!own}
              testId="card-badges-more"
            >
              <span>+{hidden.length}</span>
            </PassiveBadge>
          )}
        </div>
      )}
      {ptText && (
        <PowerToughnessBox
          ptText={ptText}
          modified={modified}
          instanceId={card.instanceId}
          isMobile={isMobile}
          onCounterAdd={onCounterAdd}
          onCounterRemove={onCounterRemove}
        />
      )}
      {primaryType !== null && (
        <CounterValueBox
          type={primaryType}
          count={counters[primaryType] > 0 ? counters[primaryType] : 0}
          instanceId={card.instanceId}
          isMobile={isMobile}
          onCounterAdd={onCounterAdd}
          onCounterRemove={onCounterRemove}
        />
      )}
    </div>
  );
}

/**
 * Reine Anzeige mit Namen als Tooltip. In einer eigenen Karte kein eigener Tab-Halt
 * (`tabIndex -1`): die Karte selbst ist schon fokussierbar, ein Halt je Marke dazwischen machte
 * den Weg übers Brett lang. Fremde Karten sind nicht fokussierbar, dort trägt der Badge den Halt.
 */
function PassiveBadge({ label, ariaLabel, focusable, testId, children }) {
  return (
    <Tooltip label={label}>
      <span
        className="battlefield-card__badge"
        role="img"
        tabIndex={focusable ? 0 : -1}
        aria-label={ariaLabel}
        data-testid={testId}
      >
        {children}
      </span>
    </Tooltip>
  );
}

/**
 * Zeiger-Handler eines Counter-Knopfs (Badge und Stärke/Widerstandskraft-Kästchen): Klick +1,
 * Rechtsklick −1. Am Maus-Pfad erreicht der Druck die Karte nicht — weder `pointerdown`
 * (Stapelung) noch `mousedown`, auf das dnd-kits Maus-Sensor hört; ein Wackeln ab 5 px startete
 * sonst den Karten-Zug und schluckte den Klick. Finger und Stift laufen durch: deren Langdruck
 * öffnet das Karten-Sheet, das auch das Verringern anbietet, und Touch meldet den Langdruck
 * zusätzlich als `contextmenu`, das hier deshalb nichts verringert. Der Merker gilt je Knopf,
 * damit ein Finger auf einem Knopf nicht das Verhalten des nächsten bestimmt.
 */
function useCounterButton(instanceId, type, onCounterAdd, onCounterRemove) {
  const touchRef = useRef(false);
  const stopForMouse = (e) => {
    if (!touchRef.current) e.stopPropagation();
  };
  return {
    onPointerDown: (e) => {
      touchRef.current = isTouchLike(e);
      stopForMouse(e);
    },
    onMouseDown: stopForMouse,
    onClick: (e) => {
      e.stopPropagation();
      onCounterAdd(instanceId, type);
    },
    onContextMenu: (e) => {
      e.preventDefault();
      if (touchRef.current) return;
      e.stopPropagation();
      onCounterRemove?.(instanceId, type);
    },
  };
}

function CounterBadge({ entry, instanceId, isMobile, onCounterAdd, onCounterRemove }) {
  const { type, label, text, icon: Icon } = entry;
  const action = isMobile ? 'Tippen +1' : 'Klick +1, Rechtsklick −1';
  const handlers = useCounterButton(instanceId, type, onCounterAdd, onCounterRemove);
  return (
    <Tooltip label={label}>
      <button
        type="button"
        className="battlefield-card__badge battlefield-card__badge--counter"
        aria-label={`${label} ${text} — ${action}`}
        {...handlers}
      >
        <Icon className="battlefield-card__badge-icon" aria-hidden />
        {text && <span>{text}</span>}
      </button>
    </Tooltip>
  );
}

/**
 * Gemeinsame Hülle der Werte-Kästchen unten rechts: mit `onCounterAdd` (eigene Karte) ein Knopf für
 * Marken des Typs `counterType`, sonst reine Anzeige. `valueLabel` benennt Wert und Kästchen,
 * `actionLabel` die Marke, die der Klick hinzufügt bzw. der Rechtsklick entfernt.
 */
function ValueBox({ testId, attrs, className, valueLabel, actionLabel, counterType, instanceId, isMobile, onCounterAdd, onCounterRemove, children }) {
  const handlers = useCounterButton(instanceId, counterType, onCounterAdd, onCounterRemove);
  const common = { 'data-testid': testId, ...attrs };
  if (!onCounterAdd) {
    return (
      <span {...common} className={['battlefield-card__pt', className].filter(Boolean).join(' ')} role="img" aria-label={valueLabel}>
        {children}
      </span>
    );
  }
  const action = isMobile ? `Tippen ${actionLabel} hinzufügen` : `Klick ${actionLabel} hinzufügen, Rechtsklick ${actionLabel} entfernen`;
  return (
    <button
      type="button"
      {...common}
      className={['battlefield-card__pt', 'battlefield-card__pt--button', className].filter(Boolean).join(' ')}
      aria-label={`${valueLabel} — ${action}`}
      {...handlers}
    >
      {children}
    </button>
  );
}

/**
 * Stärke/Widerstandskraft-Kästchen. Mit `onCounterAdd` (eigene Karte) ein Knopf für +1/+1-Counter
 * — auch ohne Counter, der erste Klick legt den ersten; sonst reine Anzeige. −1/−1-Counter
 * bleiben im Kartenmenü.
 */
function PowerToughnessBox({ ptText, modified, instanceId, isMobile, onCounterAdd, onCounterRemove }) {
  return (
    <ValueBox
      testId="card-pt"
      attrs={{ 'data-modified': modified ? 'true' : undefined }}
      className=""
      valueLabel={`Stärke/Widerstandskraft ${ptText}`}
      actionLabel="+1/+1"
      counterType="+1/+1"
      instanceId={instanceId}
      isMobile={isMobile}
      onCounterAdd={onCounterAdd}
      onCounterRemove={onCounterRemove}
    >
      {ptText}
    </ValueBox>
  );
}

/**
 * Hauptwert (Loyalität/Verteidigung) der sichtbaren Seite, steht wie das Stärke/Widerstandskraft-
 * Kästchen unten rechts: die Zahl im Symbol (Schild bzw. Stern), das so hoch ist wie das Kästchen
 * und dessen 1-px-Rahmen trägt.
 * Zeigt auch ohne Marken die 0.
 */
function CounterValueBox({ type, count, instanceId, isMobile, onCounterAdd, onCounterRemove }) {
  const label = COUNTER_LABELS[type] || type;
  const Icon = CARD_COUNTER_ICONS.find(([t]) => t === type)?.[1];
  return (
    <ValueBox
      testId="card-primary-counter"
      attrs={{ 'data-counter-type': type }}
      className="battlefield-card__pt--counter"
      valueLabel={`${label} ${count}`}
      actionLabel={label}
      counterType={type}
      instanceId={instanceId}
      isMobile={isMobile}
      onCounterAdd={onCounterAdd}
      onCounterRemove={onCounterRemove}
    >
      <span className={`battlefield-card__symbol battlefield-card__symbol--${type}`}>
        {/* Das Schild füllt die Breite eines Stärke/Widerstandskraft-Kästchens, der Stern bleibt quadratisch. */}
        {Icon && <Icon className="battlefield-card__symbol-icon" size="100%" preserveAspectRatio={type === 'loyalty' ? 'none' : undefined} aria-hidden />}
        <span className="battlefield-card__symbol-value">{count}</span>
      </span>
    </ValueBox>
  );
}
